import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import {
  ChevronUp,
  ChevronDown,
  Copy,
  Trash2,
  MousePointer,
  Edit3,
  RefreshCw,
  X,
} from "lucide-react";
import { useEditorStore, VIEWPORT_WIDTHS } from "../index";
import { DocumentRenderer } from "@zirro/renderer";
import { findNodeById } from "../tree-utils";
import { clampZoom, effectiveZoom, fitZoomFor } from "../zoom";
import { ZoomControl } from "./ZoomControl";
import { renderedImageRatio } from "../dom";
import type { ComponentType, SiteNode, Href } from "@zirro/schema";

export interface CanvasProps {
  renderSlot?: (node: SiteNode) => ReactNode | undefined;
  headerActions?: ReactNode;
}

export function Canvas({ renderSlot, headerActions }: CanvasProps = {}) {
  const document = useEditorStore((s) => s.document);
  const activePageId = useEditorStore((s) => s.activePageId);
  const setActivePage = useEditorStore((s) => s.setActivePage);
  const viewport = useEditorStore((s) => s.viewport);
  const mode = useEditorStore((s) => s.mode);
  const selectedNodeId = useEditorStore((s) => s.selectedNodeId);
  const hoveredNodeId = useEditorStore((s) => s.hoveredNodeId);
  const inlineEditingNodeId = useEditorStore((s) => s.inlineEditingNodeId);

  const selectNode = useEditorStore((s) => s.selectNode);
  const setHoveredNodeId = useEditorStore((s) => s.setHoveredNodeId);
  const setInlineEditingNodeId = useEditorStore((s) => s.setInlineEditingNodeId);
  const removeNode = useEditorStore((s) => s.removeNode);
  const duplicateNode = useEditorStore((s) => s.duplicateNode);
  const moveNode = useEditorStore((s) => s.moveNode);
  const addNode = useEditorStore((s) => s.addNode);
  const reorderNode = useEditorStore((s) => s.reorderNode);
  const updateNodeProps = useEditorStore((s) => s.updateNodeProps);
  const setInspectorTab = useEditorStore((s) => s.setInspectorTab);
  const openMediaModal = useEditorStore((s) => s.openMediaModal);
  const replaceImage = useEditorStore((s) => s.replaceImage);
  const uploadImage = useEditorStore((s) => s.uploadImage);
  const zoomSetting = useEditorStore((s) => s.zoom);
  const fitZoom = useEditorStore((s) => s.fitZoom);
  const setFitZoom = useEditorStore((s) => s.setFitZoom);

  const [isDragOver, setIsDragOver] = useState(false);
  const [dropError, setDropError] = useState<string | null>(null);
  const dropTargetRef = useRef<HTMLElement | null>(null);
  const areaRef = useRef<HTMLElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  // The page point under the cursor during a pinch, kept in place after zooming.
  const anchorRef = useRef<{ x: number; y: number; clientX: number; clientY: number } | null>(null);

  const width = VIEWPORT_WIDTHS[viewport];
  const zoom = effectiveZoom(zoomSetting, fitZoom);
  const activePage = document.pages.find((p) => p.id === activePageId);
  const selectedSearch = selectedNodeId ? findNodeById(document, selectedNodeId) : null;
  const selectedNode = selectedSearch?.node;

  // Measure the scale that fits the page into the canvas.
  useEffect(() => {
    const area = areaRef.current;
    if (!area) return;
    const measure = () => {
      const styles = getComputedStyle(area);
      const padding = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
      setFitZoom(fitZoomFor(area.clientWidth - padding, width));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(area);
    return () => observer.disconnect();
  }, [width, setFitZoom]);

  // Pinch on a trackpad, or Ctrl/Cmd + scroll, zooms around the cursor.
  useEffect(() => {
    const area = areaRef.current;
    if (!area) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const state = useEditorStore.getState();
      const current = effectiveZoom(state.zoom, state.fitZoom);
      // Trackpad pinches send small deltas; cap mouse-wheel notches to ~25% a step.
      const delta = Math.max(-25, Math.min(25, e.deltaY));
      const next = clampZoom(current * Math.exp(-delta * 0.01));
      if (next === current) return;
      const rect = frameRef.current?.getBoundingClientRect();
      if (rect) {
        anchorRef.current = {
          x: (e.clientX - rect.left) / current,
          y: (e.clientY - rect.top) / current,
          clientX: e.clientX,
          clientY: e.clientY,
        };
      }
      state.setZoom(next);
    };
    area.addEventListener("wheel", onWheel, { passive: false });
    return () => area.removeEventListener("wheel", onWheel);
  }, []);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const area = areaRef.current;
    const rect = frameRef.current?.getBoundingClientRect();
    anchorRef.current = null;
    if (!anchor || !area || !rect) return;
    area.scrollTo({
      left: area.scrollLeft + rect.left + anchor.x * zoom - anchor.clientX,
      top: area.scrollTop + rect.top + anchor.y * zoom - anchor.clientY,
      behavior: "instant",
    });
  }, [zoom]);

  const handleNavigate = (href: Href) => {
    if (href.type === "page" && href.pageId) {
      const target = document.pages.find((p) => p.id === href.pageId);
      if (target) {
        setActivePage(target.id);
        const frame = window.document.querySelector(".zirro-canvas-area");
        frame?.scrollTo?.({ top: 0, behavior: "smooth" });
      }
    } else if (href.type === "url" && href.url) {
      if (href.url === "/" || href.url === "") {
        const home = document.pages.find((p) => p.slug === "/") ?? document.pages[0];
        if (home) setActivePage(home.id);
      } else if (href.url.startsWith("/")) {
        const clean = href.url.replace(/^\//, "").replace(/\/$/, "");
        const matching = document.pages.find((p) => p.slug.replace(/^\//, "").replace(/\/$/, "") === clean);
        if (matching) {
          setActivePage(matching.id);
        } else if (href.url.includes("/shop")) {
          const shop = document.pages.find((p) => p.slug === "/shop");
          if (shop) setActivePage(shop.id);
        }
      } else if (href.url.startsWith("#")) {
        const el = window.document.getElementById(href.url.slice(1));
        el?.scrollIntoView({ behavior: "smooth" });
      } else if (href.url.startsWith("http://") || href.url.startsWith("https://") || href.url.startsWith("mailto:") || href.url.startsWith("tel:")) {
        window.open(href.url, "_blank", "noopener,noreferrer");
      }
    }
  };

  useEffect(() => {
    if (!dropError) return;
    const timer = setTimeout(() => setDropError(null), 4000);
    return () => clearTimeout(timer);
  }, [dropError]);

  /** The node a dropped photo would replace: an image, or a section's background. */
  const photoTargetAt = (target: EventTarget | null): string | null => {
    const element = (target as HTMLElement | null)?.closest?.("[data-node-id]");
    const id = element?.getAttribute("data-node-id");
    if (!id) return null;
    const found = findNodeById(document, id);
    if (!found) return null;
    if (found.node.type === "Image") return id;
    if (found.node.type === "Section" && found.node.props?.backgroundImage) return id;
    return null;
  };

  const markDropTarget = (id: string | null) => {
    const next = id
      ? (window.document.querySelector(`.zirro-canvas-frame [data-node-id="${CSS.escape(id)}"]`) as HTMLElement | null)
      : null;
    if (dropTargetRef.current === next) return;
    dropTargetRef.current?.removeAttribute("data-drop-replace");
    next?.setAttribute("data-drop-replace", "");
    dropTargetRef.current = next;
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    if (!isDragOver) setIsDragOver(true);
    // Photos from the computer highlight the image they'd replace.
    markDropTarget(e.dataTransfer.types.includes("Files") ? photoTargetAt(e.target) : null);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
    markDropTarget(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    markDropTarget(null);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      const targetId = photoTargetAt(e.target);
      if (!targetId) {
        setDropError("Drop the photo onto an image to replace it.");
        return;
      }
      const ratio = renderedImageRatio(targetId);
      setDropError(null);
      uploadImage(file)
        .then((url) => {
          replaceImage(targetId, url, ratio);
          selectNode(targetId);
        })
        .catch((err: unknown) => setDropError(err instanceof Error ? err.message : "Upload failed."));
      return;
    }

    const componentType = e.dataTransfer.getData("application/zirro-component") as ComponentType;
    const draggedNodeId = e.dataTransfer.getData("application/zirro-node-id");

    const targetElement = (e.target as HTMLElement).closest("[data-node-id]");
    const targetNodeId = targetElement?.getAttribute("data-node-id");

    if (draggedNodeId && targetNodeId && draggedNodeId !== targetNodeId) {
      // Reorder existing node
      reorderNode(draggedNodeId, targetNodeId, "after");
    } else if (componentType) {
      // Add new node from palette
      addNode(targetNodeId ?? selectedNodeId, componentType);
    }
  };

  const isTextLike =
    selectedNode &&
    ["Heading", "Text", "Button", "Badge", "Card"].includes(selectedNode.type);

  return (
    <div className="zirro-canvas-shell">
    <main
      ref={areaRef}
      className="zirro-canvas-area"
      aria-label="Canvas"
      onClick={() => {
        if (selectedNodeId) selectNode(null);
      }}
    >
      <div
        ref={frameRef}
        className={`zirro-canvas-frame ${isDragOver ? "zirro-canvas-frame--dragover" : ""}`}
        style={{ width: width * zoom }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={(e) => e.stopPropagation()}
      >
        {/* The page lays out at the device width, then is scaled to the zoom. */}
        <div className="zirro-canvas-page" style={{ width, zoom }}>
        <DocumentRenderer
          document={document}
          mode={mode}
          breakpoint={viewport}
          activePage={activePage ?? document.pages[0]}
          selectedNodeId={selectedNodeId}
          onSelectNode={selectNode}
          hoveredNodeId={hoveredNodeId}
          onHoverNode={setHoveredNodeId}
          inlineEditingNodeId={inlineEditingNodeId}
          onInlineEditChange={setInlineEditingNodeId}
          onUpdateNodeProps={updateNodeProps}
          onNavigate={mode === "preview" ? handleNavigate : undefined}
          renderSlot={renderSlot}
          headerActions={headerActions}
        />
        </div>
      </div>

      {mode === "edit" && selectedNodeId && selectedNode ? (
        <div className="zirro-selection-bar" onClick={(e) => e.stopPropagation()}>
          <span className="zirro-selection-badge">
            {selectedNode.type}
          </span>
          <div className="zirro-topbar__divider" />

          {selectedNode.type === "Image" ||
          (selectedNode.type === "Section" && selectedNode.props?.backgroundImage) ? (
            <>
              <button
                type="button"
                className="zirro-ed-btn"
                onClick={() => {
                  const ratio = renderedImageRatio(selectedNodeId);
                  openMediaModal((url) => replaceImage(selectedNodeId, url, ratio));
                }}
                title="Replace the photo — the new one fills the same frame"
              >
                <RefreshCw size={13} />
                {selectedNode.type === "Image" ? "Replace" : "Replace background"}
              </button>
              <div className="zirro-topbar__divider" />
            </>
          ) : null}

          {isTextLike ? (
            <>
              <button
                type="button"
                className="zirro-ed-btn"
                onClick={() => setInlineEditingNodeId(selectedNodeId)}
                title="Double-click canvas or click here to edit text"
              >
                <Edit3 size={13} />
                Edit Text
              </button>
              <div className="zirro-topbar__divider" />
            </>
          ) : null}

          <button
            type="button"
            className="zirro-ed-btn zirro-ed-btn--icon"
            onClick={() => moveNode(selectedNodeId, "up")}
            title="Move Up"
            aria-label="Move Up"
          >
            <ChevronUp size={14} />
          </button>
          <button
            type="button"
            className="zirro-ed-btn zirro-ed-btn--icon"
            onClick={() => moveNode(selectedNodeId, "down")}
            title="Move Down"
            aria-label="Move Down"
          >
            <ChevronDown size={14} />
          </button>
          <div className="zirro-topbar__divider" />
          <button
            type="button"
            className="zirro-ed-btn zirro-ed-btn--icon"
            onClick={() => duplicateNode(selectedNodeId)}
            title="Duplicate"
            aria-label="Duplicate"
          >
            <Copy size={14} />
          </button>
          <button
            type="button"
            className="zirro-ed-btn zirro-ed-btn--icon zirro-ed-btn--danger"
            onClick={() => removeNode(selectedNodeId)}
            title="Delete"
            aria-label="Delete"
          >
            <Trash2 size={14} />
          </button>
          <div className="zirro-topbar__divider" />
          <button
            type="button"
            className="zirro-ed-btn zirro-ed-btn--icon"
            onClick={() => selectNode(null)}
            title="Deselect"
            aria-label="Deselect"
          >
            <X size={14} />
          </button>
        </div>
      ) : null}
    </main>
    {dropError ? (
      <div className="zirro-canvas-toast" role="alert" onClick={() => setDropError(null)}>
        {dropError}
      </div>
    ) : null}
    <ZoomControl />
    </div>
  );
}
